<script>
import McpClientGuide from '@app/components/McpClientGuide.vue';
import McpConnectionsList from '@app/components/McpConnectionsList.vue';
import { describeMcpConnectionBlock, isMcpPlanBlock } from '@app/config/mcpAccess';
import mcpClients from '@app/config/mcpClients';
import { describeMcpTool, MCP_TOOL_GROUPS } from '@app/config/mcpTools';
import APIMcp from '@app/services/API/Mcp';

export default {
  name: 'SettingsMcp',
  components: {
    McpClientGuide,
    McpConnectionsList,
  },
  data: () => ({
    configuration: null,
    copied: false,
    error: null,
    loading: true,
    providers: mcpClients,
    openProviderId: null,
    videoTrigger: null,
    videoOpen: false,
    activeTab: 'connect',
    openTools: {},
    tabs: [
      { id: 'connect', label: 'Connexion' },
      { id: 'usage', label: 'Cas d’usage' },
      { id: 'security', label: 'Informations supplémentaires' },
    ],
  }),
  head: { title: 'Connecteur IA (MCP) - Paramètres' },
  computed: {
    authStore() {
      return this.$store.getters['auth/store'];
    },
    currentPlan() {
      return (this.configuration && this.configuration.plan)
        || (this.authStore && this.authStore.plan)
        || null;
    },
    currentPlanLabel() {
      return this.currentPlan ? this.$t(`packs.${this.currentPlan}`) : 'votre forfait';
    },
    isAccountView() {
      return Boolean(this.$route && this.$route.name === 'account_mcp');
    },
    mcpUrl() {
      return this.configuration ? this.configuration.resource : '';
    },
    accessLevel() {
      return this.configuration ? this.configuration.access_level : 'unavailable';
    },
    isLimitedAccess() {
      return this.accessLevel === 'limited'
        || Boolean(this.configuration && this.configuration.limited_access);
    },
    isFullAccess() {
      return this.accessLevel === 'full';
    },
    connectionAllowed() {
      if (!this.configuration) return false;
      if (typeof this.configuration.connection_allowed === 'boolean') {
        return this.configuration.connection_allowed;
      }
      return Boolean(this.configuration.plan_ok && this.accessLevel !== 'unavailable');
    },
    connectionBlockMessage() {
      const reason = this.configuration && this.configuration.connection_block_reason;
      return describeMcpConnectionBlock(reason);
    },
    isPlanBlocked() {
      return Boolean(
        this.configuration
        && this.configuration.enabled
        && isMcpPlanBlock(this.configuration.connection_block_reason),
      );
    },
    hasConnectionAccess() {
      return Boolean(
        this.configuration
        && this.configuration.enabled
        && this.connectionAllowed,
      );
    },
    canManagePlan() {
      return this.$store.getters['auth/isRoleOwner'] || this.$store.getters['auth/isRoleAdmin'];
    },
    allowedToolNames() {
      return this.configuration ? this.configuration.allowed_tools || [] : [];
    },
    allowedToolDetails() {
      return this.allowedToolNames.map(describeMcpTool);
    },
    allowedToolGroups() {
      const groups = [];

      this.allowedToolDetails.forEach((tool) => {
        let group = groups.find(({ id }) => id === tool.group);
        if (!group) {
          const details = MCP_TOOL_GROUPS[tool.group] || MCP_TOOL_GROUPS.other;
          group = {
            id: tool.group,
            icon: details.icon,
            label: details.label,
            tools: [],
          };
          groups.push(group);
        }
        group.tools.push(tool);
      });

      return groups;
    },
    lockedCapabilities() {
      return [
        'Ouvrir et résumer les contenus complets',
        'Analyser les ventes, les apprenants et leurs résultats',
        'Créer ou modifier les éléments autorisés par votre rôle',
      ];
    },
    examplePrompts() {
      if (this.isLimitedAccess) {
        return [
          '« Combien de formations ai-je, dont combien sont publiées ? »',
          '« Quelles formations de mon espace abordent l’hybridation ? »',
        ];
      }

      const prompts = [];
      const tools = new Set(this.allowedToolNames);
      if (tools.has('get_training') || tools.has('fetch')) {
        prompts.push('« Résume les points clés de ma formation sur le CMS Wordpress. »');
      }
      if (tools.has('get_sales_stats')) {
        prompts.push('« Compare mes ventes de ce mois avec le mois précédent. »');
      } else if (tools.has('get_training_stats')) {
        prompts.push('« Analyse les résultats de cette formation ce mois-ci. »');
      }
      if (tools.has('create_training')) {
        prompts.push('« Prépare en brouillon une formation de 10 chapitres sur tel sujet. »');
      }
      return prompts;
    },
    securityPoints() {
      return [
        { icon: 'user-shield', text: 'L’assistant agit en votre nom : il a vos droits, jamais plus, et reste dans votre espace Teachizy.' },
        { icon: 'key', text: 'Au moment de le connecter, c’est vous qui autorisez ses accès — et vous pouvez tout couper quand vous voulez.' },
        { icon: 'sliders-h', text: 'Teachizy choisit les outils disponibles selon votre forfait et votre rôle ; vous pouvez en autoriser encore moins au moment de la connexion.' },
        { icon: 'pen', text: 'Tout ce que l’assistant crée reste en brouillon : publier ou modifier quelque chose d’important vous demande toujours une confirmation.' },
        { icon: 'paper-plane', text: 'Les données que vous demandez sont envoyées à l’assistant choisi, y compris les informations de vos apprenants si vos droits le permettent.' },
        { icon: 'ban', text: 'L’assistant ne peut rien supprimer, et l’ajout de fichiers (images, documents) se fait toujours dans Teachizy.' },
        { icon: 'exclamation-triangle', text: 'L’IA peut se tromper : vérifiez toujours les informations importantes avant de vous y fier.' },
      ];
    },
  },
  mounted() {
    this.fetchConfiguration();
  },
  methods: {
    async fetchConfiguration() {
      this.loading = true;
      this.error = null;
      try {
        this.configuration = await APIMcp.getConfiguration();
      } catch (e) {
        this.error = 'Impossible de charger les paramètres du connecteur.';
      } finally {
        this.loading = false;
      }
    },
    openVideo(event) {
      this.videoTrigger = event ? event.currentTarget : null;
      this.videoOpen = true;
    },
    closeVideo() {
      const trigger = this.videoTrigger;
      this.videoOpen = false;
      this.videoTrigger = null;
      this.$nextTick(() => {
        if (trigger && typeof trigger.focus === 'function') {
          trigger.focus();
        }
      });
    },
    onCopySuccess() {
      this.copied = true;
      this.$copySuccess();
      setTimeout(() => { this.copied = false; }, 2000);
    },
    onCopyError() {
      this.$copyError();
    },
    toggleTool(name) {
      this.$set(this.openTools, name, !this.openTools[name]);
    },
    selectTab(id) {
      if (this.loading) return;
      this.activeTab = id;
    },
    focusTab(index) {
      this.$nextTick(() => {
        const links = this.$refs.tabLinks;
        if (links && links[index]) links[index].focus();
      });
    },
    onTabKeydown(event, index) {
      if (this.loading) return;
      const deltas = {
        ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1,
      };
      let target;
      if (event.key in deltas) {
        target = (index + deltas[event.key] + this.tabs.length) % this.tabs.length;
      } else if (event.key === 'Home') {
        target = 0;
      } else if (event.key === 'End') {
        target = this.tabs.length - 1;
      } else {
        return;
      }
      event.preventDefault();
      this.activeTab = this.tabs[target].id;
      this.focusTab(target);
    },
  },
};
</script>

<template>
  <div>
    <!-- En-tête : texte à gauche, encart de statut à droite (comme la page Assistant IA) -->
    <header class="columns is-vcentered mb-5">
      <div class="column is-8">
        <component
          :is="isAccountView ? 'h2' : 'h1'"
          class="title is-4 is-size-5-mobile"
        >
          Connecteur IA (MCP)
        </component>
        <p class="subtitle is-7">
          Utilisez vos données Teachizy depuis un <strong>assistant IA</strong> (ChatGPT, Claude…).
          Il ne voit que ce que vous pouvez déjà voir vous-même. Choisissez-le ci-dessous et
          connectez-le via <strong>OAuth</strong> : vous contrôlez les permissions et pouvez tout
          couper à tout moment.
        </p>
      </div>

      <div class="column is-4 mcp-status">
        <!-- Chargement : skeleton localisé à l'encart de statut -->
        <b-skeleton v-if="loading" height="150" />

        <b-message v-else-if="error" type="is-danger">
          {{ error }}
        </b-message>

        <!-- Indisponible / bloqué -->
        <div v-else-if="!error && !hasConnectionAccess" class="box">
          <p class="is-flex is-align-items-center mb-2">
            <b-icon icon="exclamation-triangle" size="is-small" type="is-warning" class="mr-2" />
            <span class="tag is-warning has-text-weight-bold">Indisponible</span>
          </p>
          <p class="is-size-7 mb-3">
            {{ connectionBlockMessage }}
          </p>
          <b-button
            v-if="canManagePlan && isPlanBlocked"
            type="is-warning"
            expanded
            tag="router-link"
            icon-right="gem"
            :to="{ name: 'plan', query: { onglet: 'vous' } }"
          >
            Découvrir les forfaits
          </b-button>
        </div>

        <!-- Accès étendu : PRO / EXPERT -->
        <div v-else-if="isFullAccess" class="box has-background-gradient-dark-green">
          <p class="is-flex is-align-items-center mb-2">
            <b-icon icon="check-circle" size="is-small" class="mr-2 has-text-black" />
            <span class="tag is-black has-text-weight-bold">Accès étendu</span>
          </p>
          <p class="title is-6 has-text-black mb-1">
            Votre {{ currentPlanLabel }} débloque tous les outils MCP
          </p>
          <p class="is-size-7 has-text-black mb-0">
            Soit {{ allowedToolDetails.length }} outil{{ allowedToolDetails.length > 1 ? 's' : '' }}
            selon votre rôle, détaillé{{ allowedToolDetails.length > 1 ? 's' : '' }} dans l’onglet « Cas d’usage ».
          </p>
        </div>

        <!-- Accès limité : PERCENT -->
        <div v-else-if="isLimitedAccess" class="box has-background-gradient-gold">
          <p class="is-flex is-align-items-center mb-2">
            <b-icon icon="lock" size="is-small" class="mr-2 has-text-black" />
            <span class="tag is-black has-text-weight-bold">Accès limité</span>
          </p>
          <p class="title is-6 has-text-black mb-1">
            Votre {{ currentPlanLabel }} donne un aperçu du connecteur
          </p>
          <p class="is-size-7 has-text-black mb-3">
            L’assistant peut lister vos formations et chercher dans votre catalogue, sans plus.
          </p>
          <b-button
            v-if="canManagePlan"
            type="is-black"
            inverted
            expanded
            tag="router-link"
            icon-right="gem"
            :to="{ name: 'plan', query: { onglet: 'vous' } }"
          >
            Débloquer plus d’outils
          </b-button>
          <p v-else class="is-size-7 has-text-black has-text-weight-medium mb-0">
            Demandez au propriétaire de souscrire à un forfait PRO ou EXPERT.
          </p>
        </div>
      </div>
    </header>

    <!-- Onglets visibles immédiatement, mais désactivés pendant le chargement. -->
    <div class="tabs mb-5 is-size-7-mobile">
      <ul
        role="tablist"
        aria-label="Sections du connecteur IA"
        :aria-busy="loading ? 'true' : 'false'"
      >
        <li
          v-for="(tab, index) in tabs"
          :key="tab.id"
          :class="{ 'is-active': activeTab === tab.id }"
        >
          <a
            :id="`mcp-tab-${tab.id}`"
            ref="tabLinks"
            role="tab"
            class="has-text-black"
            :class="{ 'is-disabled': loading }"
            :aria-selected="activeTab === tab.id ? 'true' : 'false'"
            :aria-controls="`mcp-panel-${tab.id}`"
            :aria-disabled="loading ? 'true' : 'false'"
            :tabindex="loading ? -1 : (activeTab === tab.id ? 0 : -1)"
            @click="selectTab(tab.id)"
            @keydown="onTabKeydown($event, index)"
          >{{ tab.label }}</a>
        </li>
      </ul>
    </div>

    <div class="mcp-panels">
      <!-- Panneau : Connexion -->
      <div
        v-show="activeTab === 'connect'"
        id="mcp-panel-connect"
        role="tabpanel"
        tabindex="0"
        aria-labelledby="mcp-tab-connect"
      >
        <div v-if="loading" class="box">
          <b-skeleton height="150" />
        </div>

        <b-message v-else-if="error" type="is-danger">
          <p>{{ error }}</p>
          <b-button class="mt-3" size="is-small" @click="fetchConfiguration">
            Réessayer
          </b-button>
        </b-message>

        <b-message v-else-if="!hasConnectionAccess" type="is-warning">
          {{ connectionBlockMessage }}
        </b-message>

        <template v-else>
        <div class="box">
          <h3 class="title is-5">
            Votre adresse de connexion
          </h3>
          <b-field label="URL du serveur MCP Teachizy" class="mb-4">
            <b-field>
              <b-input :value="mcpUrl" readonly expanded />
              <p class="control">
                <b-button
                  type="is-primary"
                  :icon-left="copied ? 'check' : 'copy'"
                  v-clipboard:copy="mcpUrl"
                  v-clipboard:success="onCopySuccess"
                  v-clipboard:error="onCopyError"
                >
                  {{ copied ? 'Copiée' : 'Copier' }}
                </b-button>
              </p>
            </b-field>
          </b-field>

          <div class="content is-small mb-4">
            <ol>
              <li>Ajoutez un serveur ou connecteur MCP distant dans votre assistant.</li>
              <li>Collez cette URL, puis connectez-vous à Teachizy avec OAuth.</li>
              <li>Vérifiez les permissions et commencez une nouvelle conversation.</li>
            </ol>
          </div>

          <b-button
            type="is-text"
            icon-left="play-circle"
            @click="openVideo($event)"
          >
            Regarder la démo
          </b-button>
        </div>

        <h3 class="title is-5 mb-2 mt-6">
          Choisissez votre assistant
        </h3>
        <p class="mb-4">
          Chaque guide suit la documentation officielle actuelle de l’éditeur.
        </p>

        <div class="mcpguide">
          <b-collapse
            v-for="provider in providers"
            :key="provider.id"
            class="box p-0 mb-3"
            animation="slide"
            :open="openProviderId === provider.id"
            :aria-id="`mcp-guide-${provider.id}`"
            @open="openProviderId = provider.id"
            @close="openProviderId = (openProviderId === provider.id ? null : openProviderId)"
          >
            <template #trigger="props">
              <div
                class="is-flex is-align-items-center p-4 is-clickable"
                role="button"
                :aria-expanded="props.open"
                :aria-label="`${props.open ? 'Replier' : 'Déplier'} le guide de connexion ${provider.name}`"
              >
                <figure class="image is-32x32 mr-3 is-flex is-align-items-center is-justify-content-center">
                  <img
                    :src="provider.logo"
                    alt=""
                    aria-hidden="true"
                    class="ofit-contain"
                    width="28"
                    height="28"
                  >
                </figure>
                <div class="is-flex-grow-1">
                  <h4 class="title is-6 mb-1">
                    {{ provider.name }}
                  </h4>
                  <b-taglist class="mb-0">
                    <b-tag type="is-light" size="is-small">
                      {{ provider.surface }}
                    </b-tag>
                    <b-tag type="is-info is-light" size="is-small">
                      {{ provider.availability }}
                    </b-tag>
                  </b-taglist>
                </div>
                <b-icon :icon="props.open ? 'chevron-up' : 'chevron-down'" class="ml-3" />
              </div>
            </template>

            <div class="px-4 pb-4">
              <p class="mb-2">
                {{ provider.description }}
              </p>
              <p class="help has-text-grey-dark mb-4">
                {{ provider.requirement }}
              </p>
              <McpClientGuide :provider="provider" :mcp-url="mcpUrl" />
            </div>
          </b-collapse>
        </div>
        </template>

        <h3 class="title is-5 mb-2 mt-6">
          Vos connexions
        </h3>
        <p class="mb-4">
          Retrouvez les assistants autorisés à utiliser votre espace et révoquez immédiatement une
          connexion que vous n’utilisez plus.
        </p>
        <div class="box">
          <b-skeleton v-if="loading" height="100" />
          <b-message v-else-if="error" type="is-danger">
            {{ error }}
          </b-message>
          <McpConnectionsList v-else-if="hasConnectionAccess" />
          <b-message v-else type="is-warning">
            Les connexions seront disponibles lorsque le connecteur sera activé pour ce compte.
          </b-message>
        </div>
      </div>

      <!-- Panneau : Cas d'usage -->
      <div
        v-show="activeTab === 'usage'"
        id="mcp-panel-usage"
        role="tabpanel"
        tabindex="0"
        aria-labelledby="mcp-tab-usage"
      >
        <h3 class="title is-5 mb-4">
          Quelques exemples de prompts à utiliser avec votre assistant IA
        </h3>
        <div class="box">
          <b-skeleton v-if="loading || error" height="72" />
          <template v-else>
            <ul class="content">
              <li v-for="prompt in examplePrompts" :key="prompt">
                {{ prompt }}
              </li>
            </ul>
            <p v-if="isLimitedAccess" class="help has-text-grey-dark mb-0">
              La recherche peut localiser un élément, mais l’assistant ne peut pas ouvrir ni résumer
              son contenu complet avec l’accès limité.
            </p>
          </template>
        </div>

        <h3 class="title is-5 mb-4 mt-6">
          Les outils accessibles à votre assistant
        </h3>
        <div v-if="loading || error" class="box">
          <b-skeleton height="260" />
        </div>
        <div v-else class="columns is-multiline">
          <div v-if="isFullAccess" class="column is-12 pb-0">
            <b-message type="is-info">
              La liste ci-dessous vient directement de vos droits effectifs Teachizy. Un outil absent n’est
              pas proposé à l’assistant, même si votre forfait donne accès au catalogue étendu.
            </b-message>
          </div>

          <div
            v-for="group in allowedToolGroups"
            :key="group.id"
            class="column is-6"
          >
            <div class="box h-full">
              <h3 class="title is-6 is-flex is-align-items-center">
                <b-icon :icon="group.icon" type="is-success" class="mr-2" />
                {{ group.label }}
              </h3>
              <div
                v-for="tool in group.tools"
                :key="tool.name"
                class="mcptool"
              >
                <div
                  class="mcptool_head is-flex is-align-items-center is-justify-content-space-between py-3 px-3 is-clickable"
                  role="button"
                  tabindex="0"
                  :aria-expanded="!!openTools[tool.name]"
                  @click="toggleTool(tool.name)"
                  @keydown.enter.prevent="toggleTool(tool.name)"
                  @keydown.space.prevent="toggleTool(tool.name)"
                >
                  <span class="is-flex is-align-items-center">
                    <b-icon icon="check-circle" type="is-primary" size="is-small" class="mr-2" />
                    <span class="has-text-weight-medium">{{ tool.label }}</span>
                  </span>
                  <b-icon
                    :icon="openTools[tool.name] ? 'minus' : 'plus'"
                    size="is-small"
                    class="ml-2 has-text-info"
                  />
                </div>
                <p
                  v-show="openTools[tool.name]"
                  class="mcptool_desc is-size-7 has-background-light"
                >
                  {{ tool.description }}
                </p>
              </div>
            </div>
          </div>

          <div v-if="allowedToolGroups.length === 0" class="column is-12">
            <b-message type="is-warning">
              Aucun outil n’est actuellement autorisé pour ce compte.
            </b-message>
          </div>

          <div v-if="isLimitedAccess" class="column is-6">
            <div class="box h-full">
              <h3 class="title is-6">
                Ce que les forfaits PRO et EXPERT vous permettent de faire en plus
              </h3>
              <p v-for="capability in lockedCapabilities" :key="capability" class="media mb-4">
                <span class="media-left">
                  <b-icon icon="lock" type="is-dark" />
                </span>
                <span class="media-content">{{ capability }}</span>
              </p>
              <b-button
                v-if="canManagePlan"
                tag="router-link"
                :to="{ name: 'plan', query: { onglet: 'vous' } }"
                type="is-warning"
                icon-right="gem"
              >
                Voir les offres
              </b-button>
            </div>
          </div>

        </div>
      </div>

      <!-- Panneau : Sécurité -->
      <div
        v-show="activeTab === 'security'"
        id="mcp-panel-security"
        role="tabpanel"
        tabindex="0"
        aria-labelledby="mcp-tab-security"
      >
        <div class="box">
          <article
            v-for="(point, i) in securityPoints"
            :key="i"
            class="media is-align-items-center"
          >
            <div class="media-left p-2 bradius-8 has-background-primary-light">
              <b-icon :icon="point.icon" type="is-primary" size="is-medium" />
            </div>
            <div class="media-content">
              <p class="mb-0">{{ point.text }}</p>
            </div>
          </article>
        </div>
      </div>
    </div>

    <b-modal
      :active="videoOpen"
      :can-cancel="['escape', 'outside', 'x']"
      :width="728"
      trap-focus
      aria-role="dialog"
      aria-modal
      aria-label="Démonstration vidéo du connecteur IA Teachizy"
      @close="closeVideo"
    >
      <div v-if="videoOpen" class="box image is-16by9">
        <iframe
          class="w-full h-full pabsolute-0"
          src="https://www.youtube-nocookie.com/embed/isCb3tYFP6k?si=OkbYURNRDaXqwxrM"
          title="Démonstration du connecteur IA Teachizy"
          frameborder="0"
          loading="lazy"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerpolicy="strict-origin-when-cross-origin"
          allowfullscreen
        />
      </div>
      <div class="is-flex is-justify-content-flex-end mt-3">
        <b-button type="is-text" @click="closeVideo">
          Fermer
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<style lang="scss" scoped>
.mcp-status {
  min-height: 220px;
}

.mcp-panels {
  min-height: 400px;
}

.mcptool + .mcptool {
  border-top: 1px solid #ededed;
}

.mcptool_head {
  border-radius: 6px;
  transition: background-color 0.12s ease;

  &:hover {
    background-color: #f5f5f5;
  }
}

.tabs a.is-disabled {
  cursor: wait;
  opacity: 0.6;
  pointer-events: none;
}

.mcptool_desc {
  padding: 0.6rem 0.75rem;
  border-radius: 6px;
  margin-bottom: 0.75rem;
}
</style>
